import { useState, useEffect } from "react";
import CrudModule from "../components/CrudModule";
import { CurrencyDollar, X, PaperPlaneTilt, EnvelopeSimple, ChatCircleText, Warning, CheckCircle, DeviceMobile } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../lib/api";

const STATUS = [
  { value: "available", label: "Available" },
  { value: "on_load", label: "On load" },
  { value: "off_duty", label: "Off duty" },
];

const EMPLOYMENT = [
  { value: "owner_op", label: "Owner Operator" },
  { value: "company", label: "Company Driver" },
  { value: "owner", label: "Company Owner (me)" },
];

const PAY_METHODS = [
  { value: "per_mile", label: "$ per mile", hint: "Company drivers usually — $0.55-$0.75/mi" },
  { value: "pct_gross", label: "% of gross load revenue", hint: "e.g. 25% to O/O of every load" },
  { value: "pct_net_after_fuel", label: "% of NET (after fuel)", hint: "e.g. 50% of load minus fuel" },
  { value: "salary", label: "Flat weekly salary", hint: "Same amount every week" },
];

// ---------------------------------------------------------------------------
// DriverStatusSmsPanel — Wave 11
// Opt-in owner SMS every time a driver flips load status. Reuses the SMS
// alerts pattern from AdminLeadsPage (save phone + toggle + test).
// ---------------------------------------------------------------------------
function DriverStatusSmsPanel({ onClose }) {
  const [enabled, setEnabled] = useState(false);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/settings/driver-status-sms");
        setEnabled(!!data.enabled);
        setPhone(data.phone || "");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await client.put("/settings/driver-status-sms", { enabled, phone });
      toast.success(enabled ? "Driver status SMS ON" : "Driver status SMS OFF");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await client.post("/settings/driver-status-sms/test");
      if (data.sent) {
        toast.success(`Test SMS sent to ${data.phone}`);
      } else {
        toast.error(data.reason === "not_configured"
          ? "Twilio credentials not configured on the server"
          : `Test failed: ${data.reason || "unknown"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div
      className="mb-4 p-4 rounded-lg border border-white/10 bg-white/[0.02]"
      data-testid="driver-status-sms-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">
          Driver status SMS · real-time load updates
        </div>
        <button onClick={onClose} className="text-neutral-500 hover:text-white text-xs">
          Close
        </button>
      </div>
      {loading ? (
        <div className="text-xs text-neutral-500">Loading…</div>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="driver-status-sms-enabled"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-white">Enable SMS alerts</span>
            </label>
            <p className="basis-full text-[11px] text-neutral-500 leading-relaxed" data-testid="driver-sms-consent-text">
              By checking this box you agree to receive recurring automated SMS notifications from
              FreightPilot AI at the number provided. Consent is not a condition of purchase. Msg
              frequency varies. Msg &amp; data rates may apply. Reply STOP to cancel, HELP for help.
            </p>
            <input
              type="tel"
              data-testid="driver-status-sms-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+15551234567"
              className="flex-1 min-w-[200px] bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-1.5 px-2.5 focus:outline-none focus:border-[#FF5A1F]/60"
            />
            <button
              data-testid="driver-status-sms-save"
              onClick={save}
              disabled={saving}
              className="rounded-md px-3 py-1.5 text-xs font-semibold bg-[#FF5A1F] hover:bg-[#FF7038] text-black disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              data-testid="driver-status-sms-test"
              onClick={sendTest}
              disabled={testing || !phone}
              className="rounded-md px-3 py-1.5 text-xs bg-white/[0.05] border border-white/10 text-neutral-300 hover:text-white disabled:opacity-50"
            >
              {testing ? "Sending…" : "Test"}
            </button>
          </div>
          <div className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
            Get a text every time a driver marks a load Arrived Pickup, In Transit, Arrived Delivery, or Delivered — no waiting for email. E.164 format required.
          </div>
        </>
      )}
    </div>
  );
}



function PayConfigModal({ driver, onClose, onSaved }) {
  const [form, setForm] = useState({
    employment_type: driver.employment_type || "company",
    pay_method: driver.pay_method || "per_mile",
    pay_rate: driver.pay_rate ?? (driver.pay_method === "per_mile" ? 0.55 : 25),
    default_fuel_advances: driver.default_fuel_advances || 0,
    default_insurance: driver.default_insurance || 0,
    default_escrow: driver.default_escrow || 0,
    default_truck_lease: driver.default_truck_lease || 0,
    default_trailer_rental: driver.default_trailer_rental || 0,
  });
  const [saving, setSaving] = useState(false);

  const pm = PAY_METHODS.find((p) => p.value === form.pay_method);

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`/drivers/${driver.id}/pay-config`, form);
      toast.success(`Pay config saved for ${driver.name}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const num = (v) => parseFloat(v) || 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
         data-testid="pay-config-modal">
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-2xl bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">Payroll Setup</div>
              <div className="text-lg font-semibold text-white">{driver.name}</div>
              <div className="text-xs text-neutral-500">Truck {driver.truck_number || "—"} · {driver.phone || "—"}</div>
            </div>
            <button onClick={onClose} data-testid="pay-config-close" className="text-neutral-500 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div className="p-5 space-y-5">
            {/* Employment type */}
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Employment Type</div>
              <div className="grid grid-cols-3 gap-2">
                {EMPLOYMENT.map((e) => (
                  <button
                    key={e.value}
                    onClick={() => set("employment_type", e.value)}
                    data-testid={`pay-emp-${e.value}`}
                    className={`p-3 rounded-lg border text-sm transition-colors ${
                      form.employment_type === e.value
                        ? "border-[#FF5A1F] bg-[#FF5A1F]/[0.08] text-white"
                        : "border-white/10 text-neutral-300 hover:border-white/25"
                    }`}
                  >
                    {e.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Pay method */}
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Pay Method</div>
              <div className="space-y-2">
                {PAY_METHODS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => set("pay_method", p.value)}
                    data-testid={`pay-method-${p.value}`}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      form.pay_method === p.value
                        ? "border-[#FF5A1F] bg-[#FF5A1F]/[0.08]"
                        : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <div className={`text-sm font-medium ${form.pay_method === p.value ? "text-[#FF5A1F]" : "text-white"}`}>
                      {p.label}
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5">{p.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Pay rate */}
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
                {form.pay_method === "per_mile" ? "Rate ($ per mile)" :
                 form.pay_method === "salary" ? "Weekly Salary ($)" :
                 "Percentage (%)"}
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.pay_rate}
                onChange={(e) => set("pay_rate", num(e.target.value))}
                data-testid="pay-rate-input"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-lg text-white tabular-nums"
              />
              <div className="text-xs text-neutral-500 mt-1">
                Example: {form.pay_method === "per_mile" ? `Runs 2,400 mi @ $${form.pay_rate}/mi = $${(2400 * form.pay_rate).toFixed(2)}` :
                         form.pay_method === "salary" ? `Every week: $${form.pay_rate.toFixed(2)}` :
                         `On a $2,500 load: $${((2500 * form.pay_rate) / 100).toFixed(2)}`}
              </div>
            </div>

            {/* Default weekly deductions */}
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Default Weekly Deductions</div>
              <div className="text-xs text-neutral-500 mb-3">These pre-fill each week's payroll. You can override on the Payroll page.</div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["default_fuel_advances", "Fuel Advances"],
                  ["default_insurance", "Insurance"],
                  ["default_escrow", "Escrow"],
                  ["default_truck_lease", "Truck Lease"],
                  ["default_trailer_rental", "Trailer Rental"],
                ].map(([k, label]) => (
                  <label key={k}>
                    <div className="text-[11px] text-neutral-500 mb-1">{label}</div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form[k]}
                      onChange={(e) => set(k, num(e.target.value))}
                      data-testid={`pay-${k}`}
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded px-2 py-1.5 text-sm text-white tabular-nums"
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-white/10 bg-[#0A0A0A] flex items-center justify-between">
            <button onClick={onClose} data-testid="pay-config-cancel" className="text-sm text-neutral-500 hover:text-white">Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="pay-config-save"
              className="btn-primary rounded-full px-5 py-2 text-sm disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save pay config"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CoachingResetModal({ driver, onClose }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [done, setDone] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await client.get(`/drivers/${driver.id}/coaching-status`);
        if (alive) setStatus(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load coaching status");
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [driver.id, onClose]);

  const doReset = async () => {
    if (!window.confirm(
      `Reset coaching history for ${driver.name}? This wipes ${status?.coaching_count || 0} SMS coaching record${status?.coaching_count === 1 ? "" : "s"} and ${status?.escalation_count || 0} owner-alert record${status?.escalation_count === 1 ? "" : "s"}. Their next low-week starts fresh.`,
    )) return;
    setResetting(true);
    try {
      const { data } = await client.post(
        `/drivers/${driver.id}/reset-coaching`,
        { reason: reason.trim() },
      );
      toast.success(`Cleared coaching for ${data.driver_name}`);
      setDone(true);
      setStatus((prev) => prev ? {
        ...prev, coaching_count: 0, escalation_count: 0,
        consecutive_weeks: 0, last_coaching: null,
      } : prev);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const consecutive = status?.consecutive_weeks || 0;
  const escalated = (status?.escalation_count || 0) > 0;
  const nothingToReset = !loading && (status?.coaching_count || 0) === 0
    && (status?.escalation_count || 0) === 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
         data-testid="coaching-reset-modal">
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-lg bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#60a5fa] font-semibold mb-1 inline-flex items-center gap-1.5">
                <ChatCircleText size={11} weight="fill" /> COACHING HISTORY
              </div>
              <div className="text-lg font-semibold text-white">{driver.name}</div>
              <div className="text-xs text-neutral-500">Truck {driver.truck_number || "—"}</div>
            </div>
            <button onClick={onClose} data-testid="coaching-reset-close" className="text-neutral-500 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {loading && (
              <div className="text-sm text-neutral-500" data-testid="coaching-reset-loading">Loading history…</div>
            )}

            {!loading && status && (
              <>
                {/* Stats strip */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3 text-center">
                    <div className="text-2xl font-mono font-bold text-white" data-testid="coaching-reset-total">
                      {status.coaching_count}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Coaching pings</div>
                  </div>
                  <div className={`rounded-lg bg-white/[0.03] border p-3 text-center ${consecutive >= 3 ? "border-red-500/40" : "border-white/5"}`}>
                    <div className={`text-2xl font-mono font-bold ${consecutive >= 3 ? "text-red-400" : consecutive > 0 ? "text-amber-400" : "text-white"}`}
                         data-testid="coaching-reset-streak">
                      {consecutive}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Streak (weeks)</div>
                  </div>
                  <div className={`rounded-lg bg-white/[0.03] border p-3 text-center ${escalated ? "border-red-500/40" : "border-white/5"}`}>
                    <div className={`text-2xl font-mono font-bold ${escalated ? "text-red-400" : "text-white"}`}
                         data-testid="coaching-reset-escalations">
                      {status.escalation_count}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Owner alerts</div>
                  </div>
                </div>

                {/* Context */}
                {consecutive >= 3 && !done && (
                  <div className="rounded-lg bg-red-500/[0.08] border border-red-500/30 p-3 text-xs text-red-200 inline-flex items-start gap-2" data-testid="coaching-reset-escalation-flag">
                    <Warning size={14} weight="fill" className="text-red-400 mt-0.5 shrink-0" />
                    <span>
                      <b>{consecutive} weeks in a row</b> — the Friday sweep escalated this to the owner. If you had a face-to-face and things are back on track, reset to clear the streak.
                    </span>
                  </div>
                )}

                {status.last_coaching && !done && (
                  <div className="text-xs text-neutral-400 leading-relaxed">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Last coaching</div>
                    <div className="font-mono">
                      {status.last_coaching.iso_week}
                      {status.last_coaching?.meta?.on_time_pct != null && (
                        <span className="text-neutral-500"> · {status.last_coaching.meta.on_time_pct}% on-time</span>
                      )}
                    </div>
                  </div>
                )}

                {done ? (
                  <div className="rounded-lg bg-emerald-500/[0.08] border border-emerald-500/30 p-4 inline-flex items-center gap-2 text-sm text-emerald-200" data-testid="coaching-reset-done">
                    <CheckCircle size={16} weight="fill" className="text-emerald-400" />
                    Coaching history cleared. Next Friday sweep starts fresh.
                  </div>
                ) : nothingToReset ? (
                  <div className="text-sm text-neutral-500" data-testid="coaching-reset-empty">
                    No coaching history yet. This driver hasn't been flagged by the Friday SLA sweep.
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-neutral-400 leading-relaxed">
                      Reset the coaching streak after a face-to-face conversation resolves the issue. This wipes the ping + alert history — the Friday sweep will re-flag them only if their on-time rate stays below 70% on new loads.
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">
                        Reason (optional — surfaces on the admin audit page)
                      </label>
                      <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Face-to-face done · agreed on 30-min pickup buffer · check back in 2 weeks"
                        maxLength={500}
                        rows={2}
                        data-testid="coaching-reset-reason"
                        className="w-full bg-black/40 border border-white/10 rounded-lg text-white text-xs p-2.5 focus:outline-none focus:border-[#60a5fa]/60 resize-y"
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="p-4 border-t border-white/10 flex justify-end gap-2">
            <button
              onClick={onClose}
              data-testid="coaching-reset-cancel"
              className="text-sm text-neutral-400 hover:text-white px-4 py-2"
            >
              {done ? "Close" : "Cancel"}
            </button>
            {!done && !nothingToReset && (
              <button
                onClick={doReset}
                disabled={resetting || loading}
                data-testid="coaching-reset-confirm"
                className="text-sm rounded-md bg-[#FF5A1F] hover:bg-[#FF7038] disabled:opacity-60 text-white font-semibold px-4 py-2 inline-flex items-center gap-1.5"
              >
                {resetting ? "Resetting…" : "Reset coaching streak"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BroadcastSmsModal — Wave 12
// Fleet-wide SMS broadcast: filter (all / on-duty / by state), preview
// recipient count, confirm, fire. Rate-limited server-side to 1 broadcast
// per 5 minutes per user.
// ---------------------------------------------------------------------------
function BroadcastSmsModal({ onClose }) {
  const [message, setMessage] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [state, setState] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [templates, setTemplates] = useState({ seed: [], custom: [] });
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateLabel, setTemplateLabel] = useState("");
  const [activeTemplateId, setActiveTemplateId] = useState("");
  // Wave 14: Schedule for later
  const [scheduleForLater, setScheduleForLater] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/drivers/broadcast-sms/templates");
        setTemplates(data);
      } catch { /* silent */ }
    })();
  }, []);

  const applyTemplate = (tid) => {
    setActiveTemplateId(tid || "");
    if (!tid) return;
    const all = [...(templates.seed || []), ...(templates.custom || [])];
    const t = all.find((x) => x.id === tid);
    if (t) {
      setMessage(t.message);
      setPreview(null);
    }
  };

  const deleteTemplate = async (tid) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await client.delete(`/drivers/broadcast-sms/templates/${tid}`);
      toast.success("Template deleted");
      const { data } = await client.get("/drivers/broadcast-sms/templates");
      setTemplates(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const canFire = message.trim().length > 0 && message.length <= 280 && preview && preview.count > 0;

  const runPreview = async () => {
    if (message.trim().length === 0) {
      toast.error("Type a message first");
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await client.post("/drivers/broadcast-sms/preview", {
        message: message.trim(),
        filter_status: filterStatus,
        state: state || "",
      });
      setPreview(data);
      if (data.count === 0) toast.warning("No drivers with phone numbers match the filter");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const fire = async () => {
    // Wave 14: Schedule branch
    if (scheduleForLater) {
      if (!scheduledDateTime) {
        toast.error("Pick a date + time first");
        return;
      }
      const iso = new Date(scheduledDateTime).toISOString();
      const when = new Date(scheduledDateTime).getTime();
      const now = Date.now();
      if (when <= now + 60_000) {
        toast.error("Schedule must be at least 1 minute in the future.");
        return;
      }
      if (!window.confirm(`Schedule this SMS to ${preview.count} driver${preview.count === 1 ? "" : "s"} at ${new Date(scheduledDateTime).toLocaleString()}?`)) return;
      setSending(true);
      try {
        const { data } = await client.post("/drivers/broadcast-sms", {
          message: message.trim(),
          filter_status: filterStatus,
          state: state || "",
          template_id: activeTemplateId || null,
          scheduled_for: iso,
        });
        setResult({ scheduled: true, id: data.id, scheduled_for: data.scheduled_for });
        toast.success(`Scheduled for ${new Date(data.scheduled_for).toLocaleString()}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Schedule failed");
      } finally {
        setSending(false);
      }
      return;
    }

    if (!window.confirm(`Send this SMS to ${preview.count} driver${preview.count === 1 ? "" : "s"}?\n\nMessage:\n${message}`)) return;
    setSending(true);
    try {
      const { data } = await client.post("/drivers/broadcast-sms", {
        message: message.trim(),
        filter_status: filterStatus,
        state: state || "",
        template_id: activeTemplateId || null,
      });
      setResult(data);
      toast.success(`Sent ${data.sent}/${data.attempted} SMS`);
      // Wave 13: optionally save as template
      if (saveAsTemplate && templateLabel.trim()) {
        try {
          await client.post("/drivers/broadcast-sms/templates", {
            label: templateLabel.trim(),
            message: message.trim(),
          });
          toast.success(`Saved template "${templateLabel.trim()}"`);
        } catch (te) {
          toast.warning(`Broadcast sent but template save failed: ${te?.response?.data?.detail || "unknown"}`);
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Broadcast failed";
      toast.error(detail);
      if (e?.response?.status === 429) setResult({ rate_limited: true, message: detail });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      data-testid="broadcast-sms-modal"
      onClick={() => !sending && onClose()}
    >
      <div
        className="max-w-lg w-full bg-neutral-950 border border-white/10 rounded-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-lg text-white font-semibold mb-1">📢 Fleet broadcast SMS</div>
        <div className="text-xs text-neutral-500 mb-4">Text every driver in your fleet at once — weather closures, safety alerts, urgent routing.</div>

        {!result && (
          <>
            {/* Wave 13: Template picker · Wave 14: usage analytics */}
            {(templates.seed.length > 0 || templates.custom.length > 0) && (
              <div className="mb-4" data-testid="broadcast-template-picker">
                <label className="text-xs text-neutral-400 block mb-1">Start from a template</label>
                <select
                  onChange={(e) => applyTemplate(e.target.value)}
                  value={activeTemplateId}
                  data-testid="broadcast-template-select"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
                >
                  <option value="">— Blank message —</option>
                  {templates.seed.length > 0 && (
                    <optgroup label="Built-in">
                      {templates.seed.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}{t.use_count > 0 ? ` · used ${t.use_count}×` : ""}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {templates.custom.length > 0 && (
                    <optgroup label="Your saved templates">
                      {templates.custom.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}{t.use_count > 0 ? ` · used ${t.use_count}×` : ""}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {activeTemplateId && (() => {
                  const all = [...templates.seed, ...templates.custom];
                  const t = all.find((x) => x.id === activeTemplateId);
                  if (!t || !t.use_count) return null;
                  return (
                    <div className="mt-2 text-[11px] text-neutral-500" data-testid="broadcast-template-analytics">
                      📊 Sent <b className="text-white">{t.use_count}×</b>
                      {t.total_sent > 0 && <> · reached <b className="text-white">{t.total_sent}</b> driver{t.total_sent === 1 ? "" : "s"}</>}
                    </div>
                  );
                })()}
                {templates.custom.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {templates.custom.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => deleteTemplate(t.id)}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/5 border border-red-500/20 text-red-300 hover:bg-red-500/15"
                        title={`Delete "${t.label}"`}
                        data-testid={`broadcast-template-delete-${t.id}`}
                      >
                        ✕ {t.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <label className="text-xs text-neutral-400 block mb-1">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              maxLength={280}
              data-testid="broadcast-sms-message"
              placeholder="Snow storm alert: shut down until Friday morning. Reply here if you're stuck."
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60 resize-none"
            />
            <div className="text-[10px] text-neutral-600 text-right mt-1">{message.length}/280 chars</div>

            <label className="text-xs text-neutral-400 block mt-3 mb-1">Recipients</label>
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPreview(null); }}
              data-testid="broadcast-sms-filter"
              className="w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
            >
              <option value="all">All drivers with phone numbers</option>
              <option value="on_duty">Only on-duty / active drivers</option>
              <option value="by_state">Filter by state</option>
            </select>
            {filterStatus === "by_state" && (
              <input
                type="text"
                maxLength={2}
                value={state}
                onChange={(e) => { setState(e.target.value.toUpperCase()); setPreview(null); }}
                placeholder="TX"
                data-testid="broadcast-sms-state"
                className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
              />
            )}

            <button
              onClick={runPreview}
              disabled={previewing || !message.trim()}
              data-testid="broadcast-sms-preview"
              className="mt-3 w-full rounded-md py-2 text-xs bg-white/[0.05] border border-white/10 text-neutral-300 hover:text-white disabled:opacity-50"
            >
              {previewing ? "Loading…" : "Preview recipients"}
            </button>

            {preview && (
              <div className="mt-3 p-3 rounded-md border border-white/10 bg-white/[0.02]" data-testid="broadcast-sms-preview-result">
                <div className="text-sm text-white">
                  {preview.count === 0
                    ? "No drivers match this filter"
                    : <>Will send to <b className="text-[#FF5A1F]">{preview.count}</b> driver{preview.count === 1 ? "" : "s"}</>}
                </div>
                {preview.preview_names && preview.preview_names.length > 0 && (
                  <div className="text-[11px] text-neutral-500 mt-1">
                    First: {preview.preview_names.join(", ")}{preview.count > 5 ? ` +${preview.count - 5} more` : ""}
                  </div>
                )}
              </div>
            )}

            {/* Wave 13: Save as template checkbox */}
            {preview && preview.count > 0 && (
              <div className="mt-3 p-3 rounded-md border border-white/10 bg-white/[0.02]" data-testid="broadcast-save-template">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveAsTemplate}
                    onChange={(e) => setSaveAsTemplate(e.target.checked)}
                    data-testid="broadcast-save-template-checkbox"
                    className="accent-[#FF5A1F]"
                  />
                  <span className="text-xs text-neutral-300">💾 Also save this message as a template</span>
                </label>
                {saveAsTemplate && (
                  <input
                    type="text"
                    value={templateLabel}
                    onChange={(e) => setTemplateLabel(e.target.value)}
                    placeholder="Template name (e.g. Winter shutdown)"
                    maxLength={60}
                    data-testid="broadcast-save-template-label"
                    className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-xs py-1.5 px-2.5 focus:outline-none focus:border-[#FF5A1F]/60"
                  />
                )}
              </div>
            )}

            {/* Wave 14: Schedule for later */}
            {preview && preview.count > 0 && (
              <div className="mt-3 p-3 rounded-md border border-white/10 bg-white/[0.02]" data-testid="broadcast-schedule">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduleForLater}
                    onChange={(e) => setScheduleForLater(e.target.checked)}
                    data-testid="broadcast-schedule-checkbox"
                    className="accent-[#FF5A1F]"
                  />
                  <span className="text-xs text-neutral-300">📅 Schedule for later instead of firing now</span>
                </label>
                {scheduleForLater && (
                  <input
                    type="datetime-local"
                    value={scheduledDateTime}
                    onChange={(e) => setScheduledDateTime(e.target.value)}
                    min={new Date(Date.now() + 5 * 60_000).toISOString().slice(0, 16)}
                    data-testid="broadcast-schedule-datetime"
                    className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-md text-white text-xs py-1.5 px-2.5 focus:outline-none focus:border-[#FF5A1F]/60"
                  />
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={onClose}
                disabled={sending}
                className="text-xs px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 text-neutral-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={fire}
                disabled={sending || !canFire || (scheduleForLater && !scheduledDateTime)}
                data-testid="broadcast-sms-fire"
                className="text-xs px-4 py-2 rounded-full bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-semibold disabled:opacity-50"
              >
                {sending
                  ? (scheduleForLater ? "Scheduling…" : "Sending…")
                  : (scheduleForLater ? "📅 Schedule" : "🚀 Fire broadcast")}
              </button>
            </div>
          </>
        )}

        {result && result.scheduled && (
          <div className="space-y-3" data-testid="broadcast-scheduled-confirm">
            <div className="p-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 text-sm">
              <b className="text-emerald-300">📅 Scheduled successfully</b>
              <div className="text-neutral-300 mt-1">
                Will fire at <b className="text-white">{new Date(result.scheduled_for).toLocaleString()}</b>
              </div>
              <div className="text-[11px] text-neutral-500 mt-2">
                You can cancel it anytime from the "Scheduled broadcasts" list on the Drivers page.
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-full rounded-full py-2 text-sm bg-white/[0.05] border border-white/10 text-white hover:border-white/25"
            >
              Done
            </button>
          </div>
        )}

        {result && !result.rate_limited && !result.scheduled && (
          <div className="space-y-3" data-testid="broadcast-sms-result">
            <div className="p-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 text-sm">
              <b className="text-emerald-300">✓ Sent {result.sent}/{result.attempted} SMS</b>
              {result.failed && result.failed.length > 0 && (
                <div className="text-[11px] text-amber-300 mt-1">
                  {result.failed.length} failed
                </div>
              )}
            </div>
            {result.failed && result.failed.length > 0 && (
              <div className="max-h-40 overflow-y-auto space-y-1">
                {result.failed.map((f, i) => (
                  <div key={i} className="text-[11px] text-neutral-400 border-t border-white/5 pt-1">
                    <span className="text-white">{f.name}</span> — {f.reason}
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={onClose}
              className="w-full rounded-full py-2 text-sm bg-white/[0.05] border border-white/10 text-white hover:border-white/25"
            >
              Done
            </button>
          </div>
        )}

        {result?.rate_limited && (
          <div className="p-3 rounded-md border border-amber-500/30 bg-amber-500/5 text-sm text-amber-300">
            {result.message}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Wave 14: ScheduledBroadcastsPanel - lists pending scheduled broadcasts
// with per-row cancel button. Empty state shows helpful copy.
// ---------------------------------------------------------------------------
function ScheduledBroadcastsPanel({ onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/drivers/broadcast-sms/scheduled");
      setRows(data.scheduled || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cancel = async (id, msg) => {
    if (!window.confirm(`Cancel scheduled broadcast?\n\n"${msg.slice(0, 80)}${msg.length > 80 ? "…" : ""}"`)) return;
    try {
      await client.delete(`/drivers/broadcast-sms/scheduled/${id}`);
      toast.success("Scheduled broadcast canceled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  return (
    <div className="mb-4 p-4 rounded-lg border border-purple-500/20 bg-purple-500/[0.03]" data-testid="scheduled-broadcasts-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest text-purple-300 font-semibold">
          📅 Scheduled broadcasts · {rows.length} pending
        </div>
        <button onClick={onClose} className="text-neutral-500 hover:text-white text-xs">Close</button>
      </div>
      {loading ? (
        <div className="text-xs text-neutral-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-neutral-500">No scheduled broadcasts. Use "📢 Broadcast SMS" and check "Schedule for later" to queue one.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="flex items-start justify-between gap-3 p-2.5 rounded-md bg-white/[0.03] border border-white/10" data-testid={`scheduled-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white truncate">{r.message}</div>
                <div className="text-[10px] text-neutral-500 mt-0.5">
                  Fires <b className="text-purple-300">{new Date(r.scheduled_for).toLocaleString()}</b> · filter: {r.filter_status}
                </div>
              </div>
              <button
                onClick={() => cancel(r.id, r.message)}
                data-testid={`scheduled-cancel-${r.id}`}
                className="text-[11px] px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 shrink-0"
              >
                Cancel
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DriversPage() {
  const [payDriver, setPayDriver] = useState(null);
  const [coachDriver, setCoachDriver] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [showSmsPanel, setShowSmsPanel] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showScheduled, setShowScheduled] = useState(false);

  const inviteAll = async () => {
    if (!window.confirm("Send a My Pay portal invite to every driver with an email on file? First-timers get a welcome, returning drivers get a fresh sign-in link.")) return;
    setInviting(true);
    try {
      const { data } = await client.post("/drivers/invite-all", {});
      const parts = [];
      if (data.sent) parts.push(`sent ${data.sent}`);
      if (data.skipped) parts.push(`skipped ${data.skipped} (no email)`);
      if (data.failed) parts.push(`${data.failed} failed`);
      if (parts.length === 0) parts.push("nothing to send");
      toast.success(`Invites: ${parts.join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch invite failed");
    } finally { setInviting(false); }
  };

  const inviteOne = async (row) => {
    if (!row.email) {
      toast.warning(`${row.name} has no email on file`);
      return;
    }
    try {
      const { data } = await client.post(`/drivers/${row.id}/invite`);
      if (data?.status === "sent") {
        const via = data.provider === "gmail_smtp" ? " (via your Gmail)" :
                    data.provider === "resend" ? " (via Resend)" : "";
        toast.success(`Invite sent to ${row.email}${via}`);
      } else {
        toast.error(data?.error || "Invite failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    }
  };

  const textSetupLink = async (row) => {
    if (!row.phone) {
      toast.warning(`${row.name} has no phone on file`);
      return;
    }
    try {
      const { data } = await client.post("/account/sms-settings/share", { phone: row.phone });
      if (data.sent) toast.success(`Setup link texted to ${row.name}`);
      else if (data.queued) toast.success(`Quiet hours — ${row.name}'s setup link sends after 7am`);
      else if (data.reason === "opted_out") toast.error(`${row.name} replied STOP — they need to text START first`);
      else toast.error(data.reason || "Couldn't send");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send");
    }
  };

  return (
    <>
      {/* Wave 11: Owner SMS on driver status change */}
      <div className="mb-4 flex items-center justify-end gap-2 flex-wrap">
        <button
          onClick={() => setShowScheduled((v) => !v)}
          data-testid="drivers-scheduled-toggle"
          className={`rounded-full px-3 py-1.5 text-[11px] border transition-colors inline-flex items-center gap-1.5 ${
            showScheduled
              ? "bg-purple-500/15 border-purple-500/40 text-purple-300"
              : "bg-white/[0.03] border-white/10 text-neutral-400 hover:border-white/25"
          }`}
        >
          📅 Scheduled broadcasts
        </button>
        <button
          onClick={() => setShowBroadcast(true)}
          data-testid="drivers-broadcast-sms-btn"
          className="rounded-full px-3 py-1.5 text-[11px] border border-white/10 bg-white/[0.03] text-neutral-300 hover:border-[#FF5A1F]/40 hover:text-[#FF9764] transition-colors inline-flex items-center gap-1.5"
        >
          📢 Broadcast SMS
        </button>
        <button
          onClick={() => setShowSmsPanel((v) => !v)}
          data-testid="drivers-sms-panel-toggle"
          className={`rounded-full px-3 py-1.5 text-[11px] border transition-colors inline-flex items-center gap-1.5 ${
            showSmsPanel
              ? "bg-[#FF5A1F]/15 border-[#FF5A1F]/40 text-[#FF9764]"
              : "bg-white/[0.03] border-white/10 text-neutral-400 hover:border-white/25"
          }`}
        >
          <ChatCircleText size={12} weight="fill" />
          Driver status SMS
        </button>
      </div>
      {showSmsPanel && (
        <DriverStatusSmsPanel onClose={() => setShowSmsPanel(false)} />
      )}
      {showScheduled && (
        <ScheduledBroadcastsPanel onClose={() => setShowScheduled(false)} />
      )}
      {showBroadcast && (
        <BroadcastSmsModal onClose={() => setShowBroadcast(false)} />
      )}

      <CrudModule
        testidPrefix="drivers"
        resource="drivers"
        overline="ROSTER"
        title="Drivers"
        subtitle="Roster, contact, and AI-drafted communications for every driver."
        aiCategory="driver_message"
        aiContextFields={["name", "truck_number", "status", "notes"]}
        primaryField="name"
        initialForm={{ status: "available" }}
        headerAction={
          <button
            onClick={inviteAll}
            disabled={inviting}
            data-testid="drivers-invite-all"
            className="rounded-full px-4 py-2 text-xs border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 inline-flex items-center gap-1.5 disabled:opacity-60 transition-colors"
          >
            <EnvelopeSimple size={12} weight="fill" /> {inviting ? "Sending..." : "Invite all to My Pay portal"}
          </button>
        }
        fields={[
          { key: "name", label: "Name", required: true },
          { key: "phone", label: "Phone", required: true },
          { key: "email", label: "Email", type: "email" },
          { key: "license_number", label: "CDL #", required: true },
          { key: "truck_number", label: "Truck #" },
          { key: "status", label: "Status", type: "select", options: STATUS },
          { key: "notes", label: "Notes", type: "textarea", span: 2 },
        ]}
        columns={[
          { key: "name", label: "NAME" },
          { key: "phone", label: "PHONE", render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
          { key: "license_number", label: "CDL", render: (r) => <span className="font-mono text-xs">{r.license_number}</span> },
          { key: "truck_number", label: "TRUCK" },
          { key: "status", label: "STATUS" },
          {
            key: "pay_method", label: "PAY",
            render: (r) => r.pay_method ? (
              <span className="text-xs text-emerald-400">
                {r.pay_method === "per_mile" ? `$${r.pay_rate}/mi` :
                 r.pay_method === "salary" ? `$${r.pay_rate}/wk` :
                 `${r.pay_rate}%`}
              </span>
            ) : <span className="text-xs text-neutral-600">not set</span>
          },
        ]}
        extraRowActions={(row) => [
          {
            icon: <CurrencyDollar size={16} weight="bold" />,
            onClick: () => setPayDriver(row),
            tooltip: "Payroll setup",
            testid: `drivers-pay-config-${row.id}`,
            color: "primary",
          },
          {
            icon: <ChatCircleText size={16} weight="bold" />,
            onClick: () => setCoachDriver(row),
            tooltip: "Coaching history · reset streak",
            testid: `drivers-coaching-${row.id}`,
            color: "info",
          },
          {
            icon: <PaperPlaneTilt size={16} weight="bold" />,
            onClick: () => inviteOne(row),
            tooltip: row.email ? "Send My Pay portal invite" : "Add an email first",
            testid: `drivers-invite-${row.id}`,
            color: "emerald",
          },
          {
            icon: <DeviceMobile size={16} weight="bold" />,
            onClick: () => textSetupLink(row),
            tooltip: row.phone ? "Text them the notification setup link" : "Add a phone first",
            testid: `drivers-text-setup-${row.id}`,
            color: "info",
          },
        ]}
      />

      {payDriver && (
        <PayConfigModal
          driver={payDriver}
          onClose={() => setPayDriver(null)}
          onSaved={() => window.location.reload()}
        />
      )}

      {coachDriver && (
        <CoachingResetModal
          driver={coachDriver}
          onClose={() => setCoachDriver(null)}
        />
      )}
    </>
  );
}
